<template>
  <div class="relative" tabindex="0">
    <!-- select opener and filter text input -->
    <span class="inline-block w-full rounded-md shadow-sm">
      <button type="button" aria-haspopup="listbox" aria-expanded="true" aria-labelledby="listbox-label" class="cursor-default relative w-full rounded-md border border-solid border-gray-500 text-left focus:shadow-outline focus:border-blue-400 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
        <div class="flex items-center">
          <input ref="popover" :placeholer="placeholder" :readonly="!combobox && !filter" @blur="handleBlur('input')" @click="handleClick" type="text" class="block w-full flex-grow px-3 py-2 z-10" v-model="filterText">
          <svg viewBox="0 0 60 30" aria-hidden="true" class="h-3 w-3 z-0 stroke-current text-gray-600 absolute inset-y-auto right-0 mr-2" style="stroke-width:6" :transform="open ? 'scale(1, -1)' : ''">
            <line x1="0" y1="0" x2="30" y2="30" /><line x1="30" y1="30" x2="60" y2="0" />
          </svg>
        </div>
      </button>
    </span>
    <!-- dropdown list -->
    <div ref="popover" @blur="handleBlur('popover')" class="absolute mt-1 rounded-md bg-white shadow w-full z-30 overflow-auto focus:outline-none" :class="{hidden: hideDropdown}" tabindex="0">
      <div
        v-if="multi"
        class="absolute top-0 right-0 z-20 cursor-pointer text-gray-700 text-sm px-4 hover:bg-gray-300 hover:text-blue-600"
        @click="handleClick('close')">
        close
      </div>
      <ul role="listbox" aria-labelledby="listbox-label" aria-activedescendant="listbox-item-3" class="max-h-56 rounded-md py-1 text-base leading-6 shadow-xs overflow-auto sm:text-sm sm:leading-5">
        <li v-if="filter && filterText.length > 0 && filteredOptions.length == 0" class="py-2 pl-3 pr-9 text-gray-800">'<span class="italic">{{filterText}}</span>' not found in list</li>
        <li v-else role="option" v-for="(choice,index) in filteredOptions" :key="index" @click="selectOption(choice)" class="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-900 hover:text-white hover:bg-gray-600">
          <div v-on:mouseover="handleMouseover" class="flex items-center space-x-3">
            <input v-if="multi" type="checkbox" :value="choice" v-model="localArray" class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
            <!-- TODO add a slot for an icon/image? -->
            <span class="font-normal block truncate">
              {{choice}}
            </span>
          </div>
          <!-- check for selected option -->
          <span v-if="choice === value" class="absolute inset-y-0 right-0 flex items-center pr-4">
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    combobox: { type: Boolean, default: false }, // optional to allow free text entry
    displayProperty: String, // note: use kebab case 'display-property' in template
    filter: { type: Boolean, default: false }, // option to allow text inout filtering
    multi: { type: Boolean, default: false }, // option to allow selection of multiple values
    options: Array,
    value: [String, Number, Array],
    valueProperty: String // note: use kebab case 'value-property' in template
  },
  model: {
    prop: 'value',
    event: 'selected'
  },
  data () {
    return {
      disableDropBlur: false,
      disableInputBlur: false,
      filterText: this.value || '',
      open: false,
      placeholder: '',
      localArray: [],
      selection: this.value || ''
    }
  },
  computed: {
    filteredOptions () {
      let listOptions = []
      // if display-property prop is used return the supplied property as the option list
      if (this.displayProperty) {
        listOptions = this.options.map(opt => opt[this.displayProperty])
      } else listOptions = this.options

      // if not filterable or the filter text is empty, return the entire list
      if ((!this.filter && !this.combobox) || this.filterText.length === 0) return listOptions
      else return listOptions.filter(opt => opt.toLowerCase().startsWith(this.filterText.toLowerCase()))
    },
    // filterText () {
    //   if (this.valueProperty && this.value) return this.value[this.valueProperty]
    //   else if (this.value) return this.value
    //   else return ''
    // },
    hideDropdown () {
      return !this.open || (this.combobox && this.filteredOptions.length < 1)
    }
  },
  methods: {
    // handleBlur & handleMouseover - if the mouse enters the dropdown prevent the input blur from closing it
    handleBlur (source) {
      if (source === 'input' && !this.disableInputBlur) {
        this.handleClick('close')
      } else if (source === 'popover' && !this.disableDropBlur) {
        this.handleClick('close')
        this.disableInputBlur = false
      }
    },
    handleClick (action = 'open') {
      // if a combobox, emit an answer on dropdown close
      if (this.combobox && this.open && this.filterText.length > 0) {
        this.$emit('selected', this.filterText)
        this.selection = this.filterText
      // if filterable, and the input contains a complete answer, clear the filter to show all available answers
      } else if ((this.filter || this.combobox) && !this.open && this.options.includes(this.filterText)) this.filterText = ''
      else this.filterText = this.value

      if (action === 'close') this.open = false
      else {
        this.open = !this.open
        this.disableInputBlur = false
      }
    },
    handleMouseover () {
      this.disableInputBlur = true
      this.disableDropBlur = false
      this.$refs.popover.focus()
    },
    selectOption (choice) {
      console.log(`selected: ${choice}`)
      if (this.multi) {
        let arrayPos = this.localArray.findIndex(elem => elem === choice)
        if (arrayPos === -1) { // If its not in the array, add it
          this.localArray.push(choice)
        } else { // if already selected, deselect
          this.localArray.splice(arrayPos, 1)
        }

        if (this.localArray.length === 1) this.filterText = `${this.localArray[0]}`
        else if (this.localArray.length > 1) this.filterText = `${this.localArray[0]} + ${this.localArray.length - 1}`
        else this.filterText = ''
        this.$emit('selected', this.localArray)
      } else {
        this.open = false
        this.disableDropBlur = true
        // if already selected, deselect
        if (choice === this.selection) {
          this.$emit('selected', '')
          this.filterText = ''
          this.selection = ''
        } else {
          this.filterText = choice
          this.selection = choice
          // if display-property prop is used return the options value-property
          if (this.displayProperty) {
            // find the object containing the chosen display property
            const chosenObj = this.options.find(x => x[this.displayProperty] === choice)
            // and return the value property of that object
            console.log(`emitting: ${chosenObj[this.valueProperty]}`)
            this.$emit('selected', chosenObj[this.valueProperty])
          } else {
            console.log(`emitting: ${choice}`)
            this.$emit('selected', choice)
          }
        }
      }
    }
  },
  created () {
    // The displayProperty and valueProperty props must accompany each other
    if (this.displayProperty || this.valueProperty) {
      if (!this.displayProperty || !this.valueProperty) {
        console.error('dropdown: display-property and value-property options must be used together')
      }
    }

    if (this.combobox) this.placeholder = 'Type or select an option'
    else if (this.filter) this.placeholder = 'Type to filter options'
  }
}
</script>
